<template>
  <container
    :isModal="true"
    @close-modal="closeScreen()"
    :loading="stateLoading"
  >
    <template v-slot:header>
      <h3>Importar Comprovante de Depósito AGESUL</h3>
    </template>
    <template v-slot:body>
      <form @submit.prevent="submitSave">
        <fieldset :disabled="stateLoading">
          <div class="p-formgrid p-grid">
            <div class="p-field p-col-12">
              <ul>
                <li>
                  <b>Pedido AGESUL:</b> envie o Word baixado em "Baixar Pedido
                  AGESUL", sem editar. É por ele que identificamos as licenças.
                </li>
                <li>
                  <b>Boleto e comprovante:</b> a AGESUL exige os dois em um
                  único arquivo. Envie cada um no seu campo (PDF ou imagem) que
                  nós juntamos automaticamente, com o boleto na frente.
                </li>
                <li>
                  <b>Já tem um arquivo com os dois juntos?</b> Deixe o campo
                  Boleto vazio e envie esse arquivo em Comprovante de Pagamento.
                  Se enviar só o comprovante sem o boleto, a AGESUL pode recusar
                  a solicitação.
                </li>
                <li>
                  O arquivo é anexado em todas as licenças do pedido, e o valor
                  pago (lido do comprovante) é dividido igualmente entre elas.
                </li>
              </ul>
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="Pedido AGESUL (Word)"
                :id="stateId + '_pedidoAgesul'"
                accept=".docx"
                :hasDownload="false"
              />
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="Boleto"
                :id="stateId + '_boleto'"
                accept="application/pdf,image/*"
                :hasDownload="false"
              />
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="Comprovante de Pagamento"
                :id="stateId + '_comprovante'"
                accept="application/pdf,image/*"
                :hasDownload="false"
              />
            </div>
            <div class="p-col-12">
              <Button type="submit" class="p-button-rounded" label="Importar" />
            </div>
          </div>
        </fieldset>
      </form>
      <div v-if="importReturn">
        <div v-if="importReturn.sucesso && importReturn.sucesso.length">
          <div class="sub-title">
            <h4>LICENÇAS ATUALIZADAS COM SUCESSO</h4>
          </div>
          <ul>
            <li v-for="item in importReturn.sucesso" :key="item">
              {{ item }}
            </li>
          </ul>
        </div>
        <div v-if="importReturn.erros && importReturn.erros.length">
          <div class="sub-title">
            <h4>NÃO IMPORTADAS</h4>
          </div>
          <ul>
            <li v-for="erro in importReturn.erros" :key="erro.nome">
              <b>{{ erro.nome }}</b> : {{ erro.erro }}
            </li>
          </ul>
        </div>
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, computed, Ref } from "vue";
import { useLicencaOrgaoService } from "../../../modules/business/licenca/licencaOrgaoBusiness";
import { ImportacaoDocumentoReturn } from "../../../modules/business/veiculo/importacaoVeiculoBusiness";
import { useToast } from "../../../modules/message";

interface ImportacaoPedidoAgesulState {
  importReturn: ImportacaoDocumentoReturn | null;
  imported: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    parentLoading: Boolean
  },
  setup(props, { emit }) {
    const state = reactive<ImportacaoPedidoAgesulState>({
      importReturn: null,
      imported: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || props.parentLoading;
    });

    const closeScreen = () => {
      emit("after-import", state.imported);
    };

    const fileInput = (suffix: string): HTMLInputElement | null => {
      return document.querySelector(
        "#" + state.stateId + suffix
      ) as HTMLInputElement;
    };

    const clearFiles = () => {
      [
        fileInput("_pedidoAgesul"),
        fileInput("_boleto"),
        fileInput("_comprovante")
      ].forEach((file) => {
        if (file) {
          file.value = "";
        }
      });
    };

    const submitSave = () => {
      state.importReturn = null;
      const pedidoAgesul = fileInput("_pedidoAgesul");
      const comprovante = fileInput("_comprovante");
      if (
        !pedidoAgesul ||
        !pedidoAgesul.files ||
        pedidoAgesul.files.length === 0
      ) {
        useToast().warning("É necessário o Pedido AGESUL.");
        return;
      }
      if (
        !comprovante ||
        !comprovante.files ||
        comprovante.files.length === 0
      ) {
        useToast().warning("É necessário o comprovante de pagamento.");
        return;
      }
      const formData = new FormData();
      formData.append("pedidoAgesul", pedidoAgesul.files[0]);
      formData.append("comprovante", comprovante.files[0]);
      const boleto = fileInput("_boleto");
      if (boleto && boleto.files && boleto.files.length > 0) {
        formData.append("boleto", boleto.files[0]);
      }

      const { importarPedidoAgesul, loading, data } = useLicencaOrgaoService();
      state.loadings.push(loading);
      importarPedidoAgesul(formData)
        .then(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          clearFiles();
          state.importReturn = data.value;
          if (!state.importReturn) {
            useToast().warning("Não foi possível realizar a importação.");
          } else {
            state.imported = !!state.importReturn.sucesso?.length;
            if (state.importReturn.erros && state.importReturn.erros.length) {
              useToast().warning(
                "Arquivo processado com erros. Verifique o retorno."
              );
            } else {
              useToast().success("Importação realizada com sucesso.");
            }
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    return {
      stateLoading,
      closeScreen,
      submitSave,
      ...toRefs(state)
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss" />
